/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import {
  DEFAULT_MAX_API_CALL_ENTRY_BYTES,
  DEFAULT_MAX_RECENT_API_CALLS,
  EXCLUDED_API_CALL_NAMES,
  EXCLUDED_API_CALL_PATH_FRAGMENTS,
} from './constants';

let enabled = false;
let maxEntries = DEFAULT_MAX_RECENT_API_CALLS;
let maxEntryBytes = DEFAULT_MAX_API_CALL_ENTRY_BYTES;
const entries = [];
const listeners = new Set();

const notify = () => {
  listeners.forEach(listener => {
    listener?.(getRecentApiCalls());
  });
};

const byteLengthUtf8 = value => {
  if (typeof value !== 'string') {
    return 0;
  }

  return new TextEncoder().encode(value).length;
};

const truncateQueryParams = value => {
  if (value === null || value === undefined) {
    return value;
  }

  let serialized;

  try {
    serialized = JSON.stringify(value);
  } catch (_) {
    return '[unserializable]';
  }

  if (byteLengthUtf8(serialized) <= maxEntryBytes) {
    return value;
  }

  if (Array.isArray(value)) {
    const trimmed = [];

    for (const item of value) {
      trimmed.push(item);

      if (byteLengthUtf8(JSON.stringify(trimmed)) > maxEntryBytes) {
        trimmed.pop();
        break;
      }
    }

    return trimmed;
  }

  if (typeof value === 'object') {
    const trimmed = {};
    const keys = Object.keys(value);

    for (const key of keys) {
      trimmed[key] = value[key];

      if (byteLengthUtf8(JSON.stringify(trimmed)) > maxEntryBytes) {
        delete trimmed[key];
        break;
      }
    }

    return trimmed;
  }

  const maxChars = Math.max(1, Math.floor(maxEntryBytes / 2));

  return String(value).slice(0, maxChars);
};

export const shouldRecordApiCall = ({ apiName, path = '' } = {}) => {
  if (!enabled) {
    return false;
  }

  if (EXCLUDED_API_CALL_NAMES.has(apiName)) {
    return false;
  }

  const normalizedPath = String(path);

  return !EXCLUDED_API_CALL_PATH_FRAGMENTS.some(fragment => normalizedPath.includes(fragment));
};

export const setApiCallHistoryConfig = ({
  enabled: nextEnabled,
  maxRecentApiCalls,
  maxEntryBytes: nextMaxEntryBytes,
} = {}) => {
  if (typeof nextEnabled === 'boolean') {
    enabled = nextEnabled;
  }

  if (typeof maxRecentApiCalls === 'number' && maxRecentApiCalls > 0) {
    maxEntries = maxRecentApiCalls;
  }

  if (typeof nextMaxEntryBytes === 'number' && nextMaxEntryBytes > 0) {
    maxEntryBytes = nextMaxEntryBytes;
  }

  if (!enabled) {
    entries.length = 0;
    notify();
  }
};

export const recordApiCall = ({ apiName, method, path = '', queryParams = {} } = {}) => {
  if (!shouldRecordApiCall({ apiName, path })) {
    return;
  }

  const entry = {
    apiName,
    method,
    path,
    queryParams: truncateQueryParams(queryParams),
    capturedAt: new Date().toISOString(),
  };

  entries.unshift(entry);

  if (entries.length > maxEntries) {
    entries.length = maxEntries;
  }

  notify();
};

export const getRecentApiCalls = () => entries.map(entry => ({ ...entry }));

export const clearApiCallHistory = () => {
  entries.length = 0;
  notify();
};

export const subscribeApiCallHistory = listener => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const resetApiCallHistoryForTests = () => {
  enabled = false;
  maxEntries = DEFAULT_MAX_RECENT_API_CALLS;
  maxEntryBytes = DEFAULT_MAX_API_CALL_ENTRY_BYTES;
  entries.length = 0;
  listeners.clear();
};
